/**
 * Default 401 copy. A 401 on a policy-guarded route means the session could not be
 * verified, which is not feature-specific — features may still override it.
 */
export const DEFAULT_SESSION_DENIED_REASON =
  'Your session could not be verified. Refresh the page or open Neurodoc from Hub again.';

/** Default 403 copy when the server returned no reason of its own. */
export const DEFAULT_POLICY_DENIED_REASON =
  'You do not have permission to perform this action.';

/**
 * Generic 401/403 policy-denial parser. Nothing in here is feature-specific — every
 * action key and message is supplied by the caller, so any guarded route can reuse it.
 *
 * - 401: treated as a session/auth denial. The backend may send different error names
 *   for expired vs. missing tokens, so we don't enumerate them — a 401 on a policy-guarded
 *   route means "the session could not be verified".
 * - 403: confirmed as a real policy denial via `reason` / `error` / `denied_by`, then the
 *   reported action is matched against `expectedActions` — exactly, or by module prefix
 *   (the server may report either the full "module:child" action or just the module when
 *   the guard checks [child, module]). An empty `expectedActions` accepts any action.
 * - anything else (network error, other status): returns null so the caller's generic
 *   error handling takes over.
 *
 * @param {*} error Axios-style error
 * @param {object} opts
 * @param {string[]} [opts.expectedActions] Accepted action keys; empty/omitted accepts any.
 * @param {string} opts.defaultAction Action reported back when the server didn't name one.
 * @param {string} opts.unauthorizedReason Message used for a 401.
 * @param {string} opts.forbiddenFallbackReason Message used for a 403 with no server reason.
 * @returns {{ denied: true, reason: string, action: string } | null}
 */
export function parsePolicyDenialResponse(
  error,
  {
    expectedActions = [],
    defaultAction,
    unauthorizedReason = DEFAULT_SESSION_DENIED_REASON,
    forbiddenFallbackReason = DEFAULT_POLICY_DENIED_REASON,
  }
) {
  const status = error?.response?.status;
  const data = error?.response?.data;
  if (status === 401) {
    const serverReason =
      typeof data?.reason === 'string' && data.reason.trim()
        ? data.reason.trim()
        : typeof data?.message === 'string' && data.message.trim()
          ? data.message.trim()
          : '';
    return {
      denied: true,
      reason: serverReason || unauthorizedReason,
      action: defaultAction,
    };
  }
  if (status !== 403) return null;
  if (!data || typeof data !== 'object') return null;
  const errName = String(data.error || '').trim();
  const reason =
    typeof data.reason === 'string' && data.reason.trim()
      ? data.reason.trim()
      : typeof data.message === 'string' && data.message.trim()
        ? data.message.trim()
        : '';
  if (
    !reason &&
    errName !== 'Forbidden' &&
    errName !== 'Forbidden by policy' &&
    String(data.denied_by || '').toLowerCase() !== 'policy'
  )
    return null;

  const action = typeof data.action === 'string' ? data.action.trim() : '';
  const allowedExact = new Set(expectedActions.filter(Boolean));
  const expectedModules = expectedActions.map((a) => String(a || '').split(':')[0]).filter(Boolean);
  const actionModule = action.split(':')[0];
  const matches =
    !action ||
    allowedExact.size === 0 ||
    allowedExact.has(action) ||
    expectedModules.includes(actionModule);
  if (!matches) return null;

  return {
    denied: true,
    reason: reason || forbiddenFallbackReason,
    action: action || defaultAction,
  };
}

/**
 * Parse 403/401 from business APIs that use *Viewer=true middleware gates.
 *
 * Positional convenience wrapper over {@link parsePolicyDenialResponse}, kept so the
 * many existing `(error, expectedAction, defaultReason)` call sites keep working.
 * Behaviour now matches every other policy flow: any 401 is a session denial, and a
 * 403's action matches exactly or by module prefix.
 */
export function parsePolicyViewerDenial(error, expectedAction, defaultReason) {
  const expected = String(expectedAction || '').trim();
  return parsePolicyDenialResponse(error, {
    expectedActions: expected ? [expected] : [],
    defaultAction: expected,
    forbiddenFallbackReason: defaultReason || 'You do not have access to perform this action.',
  });
}
